import { ANALYTICS_EVENTS } from "./events";

export interface CapturedAnalyticsEvent {
  event: string;
  properties?: Record<string, unknown>;
}

export interface OnboardingEventRequirement {
  event: string;
  match?: Record<string, unknown>;
}

export interface OnboardingEventContract {
  id: string;
  description: string;
  required: OnboardingEventRequirement[];
  forbidden?: string[];
  ordered?: string[];
}

export const ONBOARDING_EVENT_CONTRACTS = {
  talentNetworkPhoneSignIn: {
    id: "talent_network_phone_sign_in",
    description: "Phone sign-in records proof and login after successful verification without identifying the candidate in PostHog; membership is recorded by the backend.",
    required: [
      { event: ANALYTICS_EVENTS.AUD_RESUME_VERIFICATION, match: { method: "phone_otp", source: "talent_network" } },
      { event: ANALYTICS_EVENTS.AUD_RESUME_LOGIN, match: { method: "phone_otp", source: "talent_network" } },
    ],
    ordered: [ANALYTICS_EVENTS.AUD_RESUME_VERIFICATION, ANALYTICS_EVENTS.AUD_RESUME_LOGIN],
    forbidden: [ANALYTICS_EVENTS.AUD_TALENT_NETWORK_MEMBER_ADDED, "$identify"],
  },
  candidatePhoneVerification: {
    id: "candidate_phone_verification",
    description: "Phone proof is recorded independently of email verification, without identifying the candidate in PostHog.",
    required: [{ event: ANALYTICS_EVENTS.AUD_RESUME_VERIFICATION, match: { method: "phone_otp" } }],
    forbidden: ["$identify"],
  },
  candidateEmailVerification: {
    id: "candidate_email_verification",
    description: "Opening a valid email link records email proof on that device without identifying the candidate in PostHog.",
    required: [{ event: ANALYTICS_EVENTS.AUD_RESUME_VERIFICATION, match: { method: "email_link" } }],
    forbidden: ["$identify"],
  },
  normalSignupCompletion: {
    id: "normal_signup_completion",
    description: "Normal signup should emit signup and profile builder events in order.",
    required: [
      { event: ANALYTICS_EVENTS.CR_SIGNUP },
      { event: ANALYTICS_EVENTS.CR_PROFILE_BUILDER },
    ],
    ordered: [
      ANALYTICS_EVENTS.CR_SIGNUP,
      ANALYTICS_EVENTS.CR_PROFILE_BUILDER,
    ],
  },
  normalSignupManualNicheRecovery: {
    id: "normal_signup_manual_niche_recovery",
    description: "Normal signup with missing niche/keywords should require signup and profile builder without appeal.",
    required: [
      { event: ANALYTICS_EVENTS.CR_SIGNUP },
      { event: ANALYTICS_EVENTS.CR_PROFILE_BUILDER },
    ],
    forbidden: [ANALYTICS_EVENTS.CR_PROFILE_APPEAL],
    ordered: [
      ANALYTICS_EVENTS.CR_SIGNUP,
      ANALYTICS_EVENTS.CR_PROFILE_BUILDER,
    ],
  },
  draftClaimCompletion: {
    id: "draft_claim_completion",
    description: "Draft claim should emit signup and profile builder using the scraped platform.",
    required: [
      {
        event: ANALYTICS_EVENTS.CR_SIGNUP,
        match: {
          social_media: "youtube",
          primary_social_account: "youtube",
        },
      },
      { event: ANALYTICS_EVENTS.CR_PROFILE_BUILDER },
    ],
    ordered: [
      ANALYTICS_EVENTS.CR_SIGNUP,
      ANALYTICS_EVENTS.CR_PROFILE_BUILDER,
    ],
  },
  draftClaimManualNicheRecovery: {
    id: "draft_claim_manual_niche_recovery",
    description: "Draft claim missing niche/keywords should still emit signup and profile builder before niche recovery.",
    required: [
      {
        event: ANALYTICS_EVENTS.CR_SIGNUP,
        match: {
          social_media: "instagram",
          primary_social_account: "instagram",
        },
      },
      { event: ANALYTICS_EVENTS.CR_PROFILE_BUILDER },
    ],
    forbidden: [ANALYTICS_EVENTS.CR_PROFILE_APPEAL],
    ordered: [
      ANALYTICS_EVENTS.CR_SIGNUP,
      ANALYTICS_EVENTS.CR_PROFILE_BUILDER,
    ],
  },
  guestClaimApprovedPath: {
    id: "guest_claim_approved_path",
    description: "Guest claim approved path should emit signup and approved profile builder.",
    required: [
      { event: ANALYTICS_EVENTS.CR_SIGNUP },
      {
        event: ANALYTICS_EVENTS.CR_PROFILE_BUILDER,
        match: {
          status: "approved",
        },
      },
    ],
    ordered: [
      ANALYTICS_EVENTS.CR_SIGNUP,
      ANALYTICS_EVENTS.CR_PROFILE_BUILDER,
    ],
  },
  guestClaimManualNicheRecovery: {
    id: "guest_claim_manual_niche_recovery",
    description:
      "Guest claim with missing niche recovery should still emit signup and profile builder, but not appeal.",
    required: [
      { event: ANALYTICS_EVENTS.CR_SIGNUP },
      { event: ANALYTICS_EVENTS.CR_PROFILE_BUILDER },
    ],
    forbidden: [ANALYTICS_EVENTS.CR_PROFILE_APPEAL],
    ordered: [
      ANALYTICS_EVENTS.CR_SIGNUP,
      ANALYTICS_EVENTS.CR_PROFILE_BUILDER,
    ],
  },
  guestClaimAppealPath: {
    id: "guest_claim_appeal_path",
    description: "Guest claim rejected path may emit signup and builder, but not appeal until the user submits it.",
    required: [
      { event: ANALYTICS_EVENTS.CR_SIGNUP },
      {
        event: ANALYTICS_EVENTS.CR_PROFILE_BUILDER,
        match: {
          status: "rejected",
        },
      },
    ],
    forbidden: [ANALYTICS_EVENTS.CR_PROFILE_APPEAL],
    ordered: [
      ANALYTICS_EVENTS.CR_SIGNUP,
      ANALYTICS_EVENTS.CR_PROFILE_BUILDER,
    ],
  },
  manualNicheSubmission: {
    id: "manual_niche_submission",
    description: "Manual niche submission should emit the niche manual event with niche and keyword payload.",
    required: [
      {
        event: ANALYTICS_EVENTS.CR_NICHE_MANUAL,
        match: {
          primary_niche: "Technology & Software",
        },
      },
    ],
  },
  manualBeehiivSource: {
    id: "manual_beehiiv_source",
    description:
      "Manual Beehiiv onboarding should preserve the selected source value.",
    required: [
      {
        event: ANALYTICS_EVENTS.CR_SOCIALMEDIA_MANUAL,
        match: { social_media: "beehiiv" },
      },
    ],
  },
  manualMediumSource: {
    id: "manual_medium_source",
    description:
      "Manual Medium onboarding should preserve the selected source value.",
    required: [
      {
        event: ANALYTICS_EVENTS.CR_SOCIALMEDIA_MANUAL,
        match: { social_media: "medium" },
      },
    ],
  },
  manualWebsiteSource: {
    id: "manual_website_source",
    description:
      "Manual Website onboarding should preserve the selected source value.",
    required: [
      {
        event: ANALYTICS_EVENTS.CR_SOCIALMEDIA_MANUAL,
        match: { social_media: "website" },
      },
    ],
  },
  appealSubmission: {
    id: "appeal_submission",
    description: "Submitting an appeal should only emit the appeal event once the request is actually sent.",
    required: [
      { event: ANALYTICS_EVENTS.CR_PROFILE_APPEAL },
    ],
    forbidden: [ANALYTICS_EVENTS.CR_SIGNUP],
  },
  callbackFailure: {
    id: "callback_failure",
    description: "A failed callback should not emit signup, builder, or appeal events.",
    required: [],
    forbidden: [
      ANALYTICS_EVENTS.CR_SIGNUP,
      ANALYTICS_EVENTS.CR_PROFILE_BUILDER,
      ANALYTICS_EVENTS.CR_PROFILE_APPEAL,
    ],
  },
} as const satisfies Record<string, OnboardingEventContract>;
